<template>
  <div>
    <!-- Page Header & Breadcrumb -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="page-header mb-0">{{ $t('calculationMenu.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          { text: $t('calculationMenu.pageTitle'), active: true }
        ]"
      />
    </div>

    <ts-card>
      <ts-loading-banner :loading="loading">
        <ts-card-wrapper>
          <!-- Quick Filter Criteria Bar (Compact: Branch -> Date -> Cycle) & Export Excel -->
          <div class="filter-panel p-2 px-3 mb-2">
            <div
              class="d-flex flex-wrap align-items-end justify-content-between"
            >
              <!-- Left Filter Items -->
              <div class="d-flex flex-wrap align-items-end">
                <!-- 1. Branch Filter (Short / Compact width) -->
                <div class="filter-item mr-3 mb-2">
                  <label class="filter-label">
                    <i class="fa fa-university mr-1 text-primary"></i>
                    {{ $t('calculationMenu.branch') }}
                  </label>
                  <a-select
                    style="width: 230px"
                    v-model="model.branch_id"
                    mode="multiple"
                    :options="branches"
                    :show-search="true"
                    :allow-clear="true"
                    :maxTagCount="1"
                    :placeholder="$t('calculationMenu.allBranches')"
                    @change="fetchData"
                  />
                </div>

                <!-- 2. Date Filter (Short / Compact width) -->
                <div class="filter-item mr-3 mb-2">
                  <!-- Month Range -->
                  <div v-if="model.scoring_cycle === 'month'">
                    <label class="filter-label">
                      <i class="fa fa-calendar mr-1 text-primary"></i>
                      {{ $t('calculationMenu.monthRange') }}
                    </label>
                    <a-range-picker
                      style="width: 220px"
                      format="MM-YYYY"
                      :placeholder="[
                        $t('calculationMenu.fromMonth'),
                        $t('calculationMenu.toMonth')
                      ]"
                      :value="model.month_range"
                      :mode="pickerMode"
                      :open="isPickerOpen"
                      :allow-clear="true"
                      @openChange="onOpenChange"
                      @panelChange="onPanelChange"
                      @change="onRangeChange"
                    />
                  </div>

                  <!-- Year Range -->
                  <div v-else>
                    <label class="filter-label">
                      <i class="fa fa-calendar mr-1 text-primary"></i>
                      {{ $t('calculationMenu.year') }}
                    </label>
                    <div class="d-flex align-items-center" style="width: 220px">
                      <a-select
                        style="width: 100px"
                        v-model="model.from_year"
                        :options="yearOptions"
                        @change="fetchData"
                      />
                      <span class="mx-1 text-muted">→</span>
                      <a-select
                        style="width: 100px"
                        v-model="model.to_year"
                        :options="yearOptions"
                        @change="fetchData"
                      />
                    </div>
                  </div>
                </div>

                <!-- 3. Cycle Filter (Placed AFTER Date as requested) -->
                <div class="filter-item mr-3 mb-2">
                  <label class="filter-label">
                    <i class="fa fa-clock mr-1 text-primary"></i>
                    {{ $t('calculationMenu.cycle') }}
                  </label>
                  <div class="d-flex align-items-center" style="height: 32px">
                    <a-radio-group
                      v-model="model.scoring_cycle"
                      @change="onCycleChange"
                    >
                      <a-radio value="month" class="mr-3">
                        {{ $t('calculationMenu.monthly') }}
                      </a-radio>
                      <a-radio value="yearly">
                        {{ $t('calculationMenu.yearly') }}
                      </a-radio>
                    </a-radio-group>
                  </div>
                </div>

                <!-- 4. Action Buttons (Calculation, Submit & Export Excel - placed after Cycle) -->
                <div class="filter-item mb-2 d-flex flex-column justify-content-end">
                  <div style="height: 32px;" class="d-flex align-items-center">
                    <ts-button
                      color="warning"
                      outline
                      class="mr-2"
                      @click="openCalculationModal"
                    >
                      <i class="fa fa-calculator mr-1"></i>
                      {{ $t('calculationMenu.calculation') || 'Calculation' }}
                    </ts-button>

                    <ts-button
                      v-if="!isYearlyReport"
                      color="primary"
                      class="mr-2"
                      :waiting="submitting"
                      @click="submitManualHeadcount"
                    >
                      <i class="fa fa-save mr-1"></i>
                      {{ $t('submit') }}
                    </ts-button>

                    <ts-button
                      color="success"
                      outline
                      :waiting="exporting"
                      @click="exportExcel"
                    >
                      <i class="fa fa-file-excel mr-1"></i>
                      {{ $t('calculationMenu.exportExcel') }}
                    </ts-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ts-card-wrapper>

        <!-- Data List / Table Display matching StudentHeadcountExcel condition -->
        <div class="overflow-auto px-3 py-2">
          <!-- 1. MONTHLY VIEW -->
          <template v-if="!isYearlyReport">
            <div
              v-for="(group, gIndex) in activeBranchGroups"
              :key="gIndex"
              class="mb-4"
            >
              <!-- Branch Section Header -->
              <div
                class="branch-header p-2 font-weight-bold"
                :style="{
                  backgroundColor: group.is_grand_total ? '#ffebee' : '#f2f2f2',
                  color: group.is_grand_total ? '#b30000' : '#002060',
                  border: '1px solid #8c8c8c',
                  borderBottom: 'none'
                }"
              >
                <i class="fa fa-building mr-1"></i>
                <span v-if="group.is_grand_total">{{ group.branch_name }}</span>
                <span v-else
                  >{{ $t('calculationMenu.branch') }}:
                  {{ group.branch_name }}</span
                >
              </div>

              <table class="table table-bordered headcount-table mb-0">
                <thead>
                  <!-- Header Row 1: Months -->
                  <tr>
                    <th
                      rowspan="2"
                      class="th-fixed text-center"
                      style="width: 45px; vertical-align: middle;"
                    >
                      SN
                    </th>
                    <th
                      rowspan="2"
                      class="th-fixed text-center"
                      style="min-width: 180px; vertical-align: middle;"
                    >
                      {{ $t('calculationMenu.program') }} /
                      {{ $t('calculationMenu.service') }}
                    </th>
                    <th
                      v-for="(m, mIndex) in reportMonths"
                      :key="mIndex"
                      :colspan="m.is_first ? 7 : 6"
                      class="text-center month-header"
                      style="background-color: #e7e6e6; font-weight: bold; font-size: 11px;"
                    >
                      {{ m.label }}
                    </th>
                  </tr>

                  <!-- Header Row 2: Sub-headers -->
                  <tr class="sub-header text-center">
                    <template v-for="(m, mIndex) in reportMonths">
                      <th
                        v-if="m.is_first"
                        :key="'old-' + mIndex"
                        class="col-old"
                      >
                        Old.Ss
                      </th>
                      <th :key="'new-' + mIndex" class="col-new">New</th>
                      <th :key="'drop-' + mIndex" class="col-drop">St.Ss</th>
                      <th :key="'cin-' + mIndex" class="col-cin">C.In</th>
                      <th :key="'cout-' + mIndex" class="col-cout">C.Out</th>
                      <th :key="'free-' + mIndex" class="col-free">Free</th>
                      <th :key="'tot-' + mIndex" class="col-total">T.Ss</th>
                    </template>
                  </tr>
                </thead>

                <tbody>
                  <!-- 1. ACADEMIC PROGRAMS SECTION -->
                  <tr class="section-title-row">
                    <td
                      :colspan="monthlyColSpan"
                      class="text-left font-weight-bold pl-2 py-1"
                      style="background-color: #f0f4f8; color: #002060; font-size: 11px;"
                    >
                      <i class="fa fa-graduation-cap mr-1 text-primary"></i>
                      <strong>{{ $t('calculationMenu.subPrograms') }}</strong>
                    </td>
                  </tr>

                  <!-- Academic Program Rows -->
                  <tr
                    v-for="(row, rIndex) in getFilteredSubPrograms(group)"
                    :key="'sub-' + rIndex"
                  >
                    <td class="text-center">{{ rIndex + 1 }}</td>
                    <td class="font-weight-bold text-left">{{ row.name }}</td>

                    <template v-for="(m, mIndex) in reportMonths">
                      <!-- Old students (first month only - calculated from previous month) -->
                      <td
                        v-if="m.is_first"
                        :key="'sub-cell-old-' + mIndex"
                        class="text-center font-weight-bold col-old"
                      >
                        <span class="cell-span">
                          {{ formatVal(row.first_old) }}
                        </span>
                      </td>

                      <!-- Monthly metrics -->
                      <td
                        :key="'sub-cell-new-' + mIndex"
                        class="text-center font-weight-bold col-new p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-new"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].new
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'new'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur($event, group, row, m.key, 'new')
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].new
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'sub-cell-drop-' + mIndex"
                        class="text-center col-drop p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-drop"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].dropped
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'dropped'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur(
                              $event,
                              group,
                              row,
                              m.key,
                              'dropped'
                            )
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].dropped
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'sub-cell-cin-' + mIndex"
                        class="text-center col-cin p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-cin"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].transfer_in
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_in'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_in'
                            )
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].transfer_in
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'sub-cell-cout-' + mIndex"
                        class="text-center col-cout p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-cout"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].transfer_out
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_out'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_out'
                            )
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].transfer_out
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'sub-cell-free-' + mIndex"
                        class="text-center col-free p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-free"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].free
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'free'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur($event, group, row, m.key, 'free')
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].free
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <!-- Total students (auto-calculated) -->
                      <td
                        :key="'sub-cell-tot-' + mIndex"
                        class="text-center font-weight-bold col-total"
                      >
                        <span class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].total
                                : 0
                            )
                          }}
                        </span>
                      </td>
                    </template>
                  </tr>

                  <!-- Academic Programs Total Row -->
                  <tr class="total-row" v-if="group.subProgramTotals">
                    <td
                      colspan="2"
                      class="text-left font-weight-bold pl-2"
                      style="color: #c00000;"
                    >
                      {{ $t('calculationMenu.total') }}:
                    </td>

                    <template v-for="(m, mIndex) in reportMonths">
                      <td
                        v-if="m.is_first"
                        :key="'subtot-old-' + mIndex"
                        class="text-center font-weight-bold col-old"
                      >
                        {{ formatVal(group.subProgramTotals.first_old) }}
                      </td>
                      <td
                        :key="'subtot-new-' + mIndex"
                        class="text-center font-weight-bold col-new"
                      >
                        {{
                          formatVal(
                            group.subProgramTotals.months &&
                              group.subProgramTotals.months[m.key]
                              ? group.subProgramTotals.months[m.key].new
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'subtot-drop-' + mIndex"
                        class="text-center font-weight-bold col-drop"
                      >
                        {{
                          formatVal(
                            group.subProgramTotals.months &&
                              group.subProgramTotals.months[m.key]
                              ? group.subProgramTotals.months[m.key].dropped
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'subtot-cin-' + mIndex"
                        class="text-center font-weight-bold col-cin"
                      >
                        {{
                          formatVal(
                            group.subProgramTotals.months &&
                              group.subProgramTotals.months[m.key]
                              ? group.subProgramTotals.months[m.key].transfer_in
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'subtot-cout-' + mIndex"
                        class="text-center font-weight-bold col-cout"
                      >
                        {{
                          formatVal(
                            group.subProgramTotals.months &&
                              group.subProgramTotals.months[m.key]
                              ? group.subProgramTotals.months[m.key]
                                  .transfer_out
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'subtot-free-' + mIndex"
                        class="text-center font-weight-bold col-free"
                      >
                        {{
                          formatVal(
                            group.subProgramTotals.months &&
                              group.subProgramTotals.months[m.key]
                              ? group.subProgramTotals.months[m.key].free
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'subtot-tot-' + mIndex"
                        class="text-center font-weight-bold col-total"
                      >
                        {{
                          formatVal(
                            group.subProgramTotals.months &&
                              group.subProgramTotals.months[m.key]
                              ? group.subProgramTotals.months[m.key].total
                              : 0
                          )
                        }}
                      </td>
                    </template>
                  </tr>

                  <!-- 2. SERVICES SECTION -->
                  <tr class="section-title-row">
                    <td
                      :colspan="monthlyColSpan"
                      class="text-left font-weight-bold pl-2 py-1"
                      style="background-color: #f0f4f8; color: #002060; font-size: 11px;"
                    >
                      <i class="fa fa-calculator mr-1 text-primary"></i>
                      <strong>{{ $t('calculationMenu.services') }}</strong>
                    </td>
                  </tr>

                  <!-- Service Rows -->
                  <tr
                    v-for="(row, rIndex) in getFilteredServices(group)"
                    :key="'srv-' + rIndex"
                  >
                    <td class="text-center">{{ rIndex + 1 }}</td>
                    <td class="font-weight-bold text-left">{{ row.name }}</td>

                    <template v-for="(m, mIndex) in reportMonths">
                      <!-- Old students (first month only - calculated from previous month) -->
                      <td
                        v-if="m.is_first"
                        :key="'srv-cell-old-' + mIndex"
                        class="text-center font-weight-bold col-old"
                      >
                        <span class="cell-span">
                          {{ formatVal(row.first_old) }}
                        </span>
                      </td>

                      <!-- Monthly metrics -->
                      <td
                        :key="'srv-cell-new-' + mIndex"
                        class="text-center font-weight-bold col-new p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-new"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].new
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'new'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur($event, group, row, m.key, 'new')
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].new
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'srv-cell-drop-' + mIndex"
                        class="text-center col-drop p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-drop"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].dropped
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'dropped'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur(
                              $event,
                              group,
                              row,
                              m.key,
                              'dropped'
                            )
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].dropped
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'srv-cell-cin-' + mIndex"
                        class="text-center col-cin p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-cin"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].transfer_in
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_in'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_in'
                            )
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].transfer_in
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'srv-cell-cout-' + mIndex"
                        class="text-center col-cout p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-cout"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].transfer_out
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_out'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur(
                              $event,
                              group,
                              row,
                              m.key,
                              'transfer_out'
                            )
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].transfer_out
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <td
                        :key="'srv-cell-free-' + mIndex"
                        class="text-center col-free p-0"
                      >
                        <input
                          v-if="!group.is_grand_total"
                          type="number"
                          class="cell-input col-free"
                          :value="
                            row.months && row.months[m.key]
                              ? row.months[m.key].free
                              : 0
                          "
                          @input="
                            onMonthlyCellChange(
                              $event,
                              group,
                              row,
                              m.key,
                              'free'
                            )
                          "
                          @blur="
                            onMonthlyCellBlur($event, group, row, m.key, 'free')
                          "
                          @focus="$event.target.select()"
                        />
                        <span v-else class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].free
                                : 0
                            )
                          }}
                        </span>
                      </td>

                      <!-- Total services (auto-calculated) -->
                      <td
                        :key="'srv-cell-tot-' + mIndex"
                        class="text-center font-weight-bold col-total"
                      >
                        <span class="cell-span">
                          {{
                            formatVal(
                              row.months && row.months[m.key]
                                ? row.months[m.key].total
                                : 0
                            )
                          }}
                        </span>
                      </td>
                    </template>
                  </tr>

                  <!-- Services Total Row -->
                  <tr class="total-row" v-if="group.serviceTotals">
                    <td
                      colspan="2"
                      class="text-left font-weight-bold pl-2"
                      style="color: #c00000;"
                    >
                      {{ $t('calculationMenu.total') }}:
                    </td>

                    <template v-for="(m, mIndex) in reportMonths">
                      <td
                        v-if="m.is_first"
                        :key="'srvtot-old-' + mIndex"
                        class="text-center font-weight-bold col-old"
                      >
                        {{ formatVal(group.serviceTotals.first_old) }}
                      </td>
                      <td
                        :key="'srvtot-new-' + mIndex"
                        class="text-center font-weight-bold col-new"
                      >
                        {{
                          formatVal(
                            group.serviceTotals.months &&
                              group.serviceTotals.months[m.key]
                              ? group.serviceTotals.months[m.key].new
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'srvtot-drop-' + mIndex"
                        class="text-center font-weight-bold col-drop"
                      >
                        {{
                          formatVal(
                            group.serviceTotals.months &&
                              group.serviceTotals.months[m.key]
                              ? group.serviceTotals.months[m.key].dropped
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'srvtot-cin-' + mIndex"
                        class="text-center font-weight-bold col-cin"
                      >
                        {{
                          formatVal(
                            group.serviceTotals.months &&
                              group.serviceTotals.months[m.key]
                              ? group.serviceTotals.months[m.key].transfer_in
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'srvtot-cout-' + mIndex"
                        class="text-center font-weight-bold col-cout"
                      >
                        {{
                          formatVal(
                            group.serviceTotals.months &&
                              group.serviceTotals.months[m.key]
                              ? group.serviceTotals.months[m.key].transfer_out
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'srvtot-free-' + mIndex"
                        class="text-center font-weight-bold col-free"
                      >
                        {{
                          formatVal(
                            group.serviceTotals.months &&
                              group.serviceTotals.months[m.key]
                              ? group.serviceTotals.months[m.key].free
                              : 0
                          )
                        }}
                      </td>
                      <td
                        :key="'srvtot-tot-' + mIndex"
                        class="text-center font-weight-bold col-total"
                      >
                        {{
                          formatVal(
                            group.serviceTotals.months &&
                              group.serviceTotals.months[m.key]
                              ? group.serviceTotals.months[m.key].total
                              : 0
                          )
                        }}
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- 2. YEARLY VIEW (Matching student-headcount-yearly.blade.php condition) -->
          <template v-else>
            <div
              v-for="(group, gIndex) in activeBranchGroups"
              :key="gIndex"
              class="mb-4"
            >
              <!-- Branch Section Header -->
              <div
                class="branch-header p-2 font-weight-bold"
                :style="{
                  backgroundColor: group.is_grand_total ? '#ffebee' : '#d9d9d9',
                  color: group.is_grand_total ? '#b30000' : '#000000',
                  border: '1px solid #7f7f7f',
                  borderBottom: 'none'
                }"
              >
                <i class="fa fa-building mr-1"></i>
                <span>{{ group.branch_name }}</span>
                <span class="float-right text-muted small" v-if="yearTitle">
                  {{ yearTitle }} ({{ startLabel }} - {{ endLabel }})
                </span>
              </div>

              <table class="table table-bordered headcount-table mb-0">
                <thead>
                  <tr class="text-center bg-light">
                    <th style="width: 45px;">SN</th>
                    <th style="min-width: 180px;">
                      {{ $t('calculationMenu.program') }} /
                      {{ $t('calculationMenu.service') }}
                    </th>
                    <th class="col-old">Old.Ss</th>
                    <th class="col-new">New</th>
                    <th class="col-drop">St.Ss</th>
                    <th class="col-cin">C.In</th>
                    <th class="col-cout">C.Out</th>
                    <th class="col-free">Free</th>
                    <th class="col-total">T.Ss</th>
                    <th class="font-weight-bold">
                      {{ $t('calculationMenu.growLoss') }}
                    </th>
                    <th class="font-weight-bold">
                      {{ $t('calculationMenu.growLossPercentage') }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <!-- 1. ACADEMIC PROGRAMS SECTION -->
                  <tr class="section-title-row">
                    <td
                      colspan="11"
                      class="text-left font-weight-bold pl-2 py-1"
                      style="background-color: #f0f4f8; color: #002060; font-size: 11px;"
                    >
                      <i class="fa fa-graduation-cap mr-1 text-primary"></i>
                      <strong>{{ $t('calculationMenu.subPrograms') }}</strong>
                    </td>
                  </tr>

                  <!-- Academic Program Rows -->
                  <tr
                    v-for="(row, rIndex) in getFilteredSubPrograms(group)"
                    :key="'ysub-' + rIndex"
                  >
                    <td class="text-center">{{ rIndex + 1 }}</td>
                    <td class="font-weight-bold text-left">{{ row.name }}</td>

                    <!-- Old.Ss (calculated from previous period) -->
                    <td class="text-center font-weight-bold col-old">
                      <span class="cell-span">{{ formatVal(row.old) }}</span>
                    </td>

                    <!-- New -->
                    <td class="text-center font-weight-bold col-new">
                      <span class="cell-span">{{ formatVal(row.new) }}</span>
                    </td>

                    <!-- St.Ss (dropped) -->
                    <td class="text-center col-drop">
                      <span class="cell-span">{{ formatVal(row.dropped) }}</span>
                    </td>

                    <!-- C.In (transfer_in) -->
                    <td class="text-center col-cin">
                      <span class="cell-span">{{ formatVal(row.transfer_in) }}</span>
                    </td>

                    <!-- C.Out (transfer_out) -->
                    <td class="text-center col-cout">
                      <span class="cell-span">{{ formatVal(row.transfer_out) }}</span>
                    </td>

                    <!-- Free -->
                    <td class="text-center col-free">
                      <span class="cell-span">{{ formatVal(row.free) }}</span>
                    </td>

                    <!-- T.Ss (total - auto-calculated) -->
                    <td class="text-center font-weight-bold col-total">
                      <span class="cell-span">{{ formatVal(row.total) }}</span>
                    </td>

                    <!-- Grow/Loss -->
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        row.grow_loss > 0
                          ? 'text-success'
                          : row.grow_loss < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        row.grow_loss > 0
                          ? '+' + row.grow_loss
                          : row.grow_loss !== 0
                          ? row.grow_loss
                          : '-'
                      }}
                    </td>

                    <!-- Grow/Loss % -->
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        row.grow_loss_percentage > 0
                          ? 'text-success'
                          : row.grow_loss_percentage < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        row.grow_loss_percentage !== 0
                          ? row.grow_loss_percentage + '%'
                          : '-'
                      }}
                    </td>
                  </tr>

                  <!-- Academic Programs Total Row -->
                  <tr class="total-row" v-if="group.subProgramTotals">
                    <td
                      colspan="2"
                      class="text-left font-weight-bold pl-2"
                      style="color: #c00000;"
                    >
                      {{ $t('calculationMenu.total') }}:
                    </td>
                    <td class="text-center font-weight-bold col-old">
                      {{ formatVal(group.subProgramTotals.old) }}
                    </td>
                    <td class="text-center font-weight-bold col-new">
                      {{ formatVal(group.subProgramTotals.new) }}
                    </td>
                    <td class="text-center font-weight-bold col-drop">
                      {{ formatVal(group.subProgramTotals.dropped) }}
                    </td>
                    <td class="text-center font-weight-bold col-cin">
                      {{ formatVal(group.subProgramTotals.transfer_in) }}
                    </td>
                    <td class="text-center font-weight-bold col-cout">
                      {{ formatVal(group.subProgramTotals.transfer_out) }}
                    </td>
                    <td class="text-center font-weight-bold col-free">
                      {{ formatVal(group.subProgramTotals.free) }}
                    </td>
                    <td class="text-center font-weight-bold col-total">
                      {{ formatVal(group.subProgramTotals.total) }}
                    </td>
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        group.subProgramTotals.grow_loss > 0
                          ? 'text-success'
                          : group.subProgramTotals.grow_loss < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        group.subProgramTotals.grow_loss > 0
                          ? '+' + group.subProgramTotals.grow_loss
                          : group.subProgramTotals.grow_loss !== 0
                          ? group.subProgramTotals.grow_loss
                          : '-'
                      }}
                    </td>
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        group.subProgramTotals.grow_loss_percentage > 0
                          ? 'text-success'
                          : group.subProgramTotals.grow_loss_percentage < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        group.subProgramTotals.grow_loss_percentage !== 0
                          ? group.subProgramTotals.grow_loss_percentage + '%'
                          : '-'
                      }}
                    </td>
                  </tr>

                  <!-- 2. SERVICES SECTION -->
                  <tr class="section-title-row">
                    <td
                      colspan="11"
                      class="text-left font-weight-bold pl-2 py-1"
                      style="background-color: #f0f4f8; color: #002060; font-size: 11px;"
                    >
                      <i class="fa fa-calculator mr-1 text-primary"></i>
                      <strong>{{ $t('calculationMenu.services') }}</strong>
                    </td>
                  </tr>

                  <!-- Service Rows -->
                  <tr
                    v-for="(row, rIndex) in getFilteredServices(group)"
                    :key="'ysrv-' + rIndex"
                  >
                    <td class="text-center">{{ rIndex + 1 }}</td>
                    <td class="font-weight-bold text-left">{{ row.name }}</td>

                    <!-- Old.Ss (calculated from previous period) -->
                    <td class="text-center font-weight-bold col-old">
                      <span class="cell-span">{{ formatVal(row.old) }}</span>
                    </td>

                    <!-- New -->
                    <td class="text-center font-weight-bold col-new">
                      <span class="cell-span">{{ formatVal(row.new) }}</span>
                    </td>

                    <!-- St.Ss (dropped) -->
                    <td class="text-center col-drop">
                      <span class="cell-span">{{ formatVal(row.dropped) }}</span>
                    </td>

                    <!-- C.In (transfer_in) -->
                    <td class="text-center col-cin">
                      <span class="cell-span">{{ formatVal(row.transfer_in) }}</span>
                    </td>

                    <!-- C.Out (transfer_out) -->
                    <td class="text-center col-cout">
                      <span class="cell-span">{{ formatVal(row.transfer_out) }}</span>
                    </td>

                    <!-- Free -->
                    <td class="text-center col-free">
                      <span class="cell-span">{{ formatVal(row.free) }}</span>
                    </td>

                    <!-- T.Ss (total - auto-calculated) -->
                    <td class="text-center font-weight-bold col-total">
                      <span class="cell-span">{{ formatVal(row.total) }}</span>
                    </td>

                    <!-- Grow/Loss -->
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        row.grow_loss > 0
                          ? 'text-success'
                          : row.grow_loss < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        row.grow_loss > 0
                          ? '+' + row.grow_loss
                          : row.grow_loss !== 0
                          ? row.grow_loss
                          : '-'
                      }}
                    </td>

                    <!-- Grow/Loss % -->
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        row.grow_loss_percentage > 0
                          ? 'text-success'
                          : row.grow_loss_percentage < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        row.grow_loss_percentage !== 0
                          ? row.grow_loss_percentage + '%'
                          : '-'
                      }}
                    </td>
                  </tr>

                  <!-- Services Total Row -->
                  <tr class="total-row" v-if="group.serviceTotals">
                    <td
                      colspan="2"
                      class="text-left font-weight-bold pl-2"
                      style="color: #c00000;"
                    >
                      {{ $t('calculationMenu.total') }}:
                    </td>
                    <td class="text-center font-weight-bold col-old">
                      {{ formatVal(group.serviceTotals.old) }}
                    </td>
                    <td class="text-center font-weight-bold col-new">
                      {{ formatVal(group.serviceTotals.new) }}
                    </td>
                    <td class="text-center font-weight-bold col-drop">
                      {{ formatVal(group.serviceTotals.dropped) }}
                    </td>
                    <td class="text-center font-weight-bold col-cin">
                      {{ formatVal(group.serviceTotals.transfer_in) }}
                    </td>
                    <td class="text-center font-weight-bold col-cout">
                      {{ formatVal(group.serviceTotals.transfer_out) }}
                    </td>
                    <td class="text-center font-weight-bold col-free">
                      {{ formatVal(group.serviceTotals.free) }}
                    </td>
                    <td class="text-center font-weight-bold col-total">
                      {{ formatVal(group.serviceTotals.total) }}
                    </td>
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        group.serviceTotals.grow_loss > 0
                          ? 'text-success'
                          : group.serviceTotals.grow_loss < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        group.serviceTotals.grow_loss > 0
                          ? '+' + group.serviceTotals.grow_loss
                          : group.serviceTotals.grow_loss !== 0
                          ? group.serviceTotals.grow_loss
                          : '-'
                      }}
                    </td>
                    <td
                      class="text-center font-weight-bold"
                      :class="
                        group.serviceTotals.grow_loss_percentage > 0
                          ? 'text-success'
                          : group.serviceTotals.grow_loss_percentage < 0
                          ? 'text-danger'
                          : 'text-muted'
                      "
                    >
                      {{
                        group.serviceTotals.grow_loss_percentage !== 0
                          ? group.serviceTotals.grow_loss_percentage + '%'
                          : '-'
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- Empty State -->
          <div
            v-if="
              !loading &&
                (!activeBranchGroups || activeBranchGroups.length === 0)
            "
            class="text-center py-5 text-muted"
          >
            <i class="fa fa-chart-bar fa-3x mb-3 text-secondary"></i>
            <h5>{{ $t('no_data') || 'No calculation data available' }}</h5>
            <p class="small">
              Try adjusting the branch filter or date range criteria above.
            </p>
          </div>
        </div>
      </ts-loading-banner>
    </ts-card>

    <!-- Calculation Modal (Popup to select cycle year and month) -->
    <a-modal
      v-model="showCalculationModal"
      :title="$t('calculationMenu.calculateTotal') || 'Calculate Total'"
      :zIndex="1050"
      :maskClosable="false"
      width="440px"
      @cancel="closeCalculationModal"
    >
      <div class="py-2">
        <!-- Informational note about locked periods -->
        <div class="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center" style="font-size: 11.5px; border-radius: 4px; border: 1px solid #ffeeba; background-color: #fff3cd; color: #856404; line-height: 1.4;">
          <i class="fa fa-info-circle mr-2 text-warning" style="font-size: 15px; flex-shrink: 0;"></i>
          <span>{{ $t('calculationMenu.lockedMonthNotice') || 'Recalculation is only allowed from September 2026 onward. Earlier months are locked.' }}</span>
        </div>

        <!-- Cycle Year -->
        <div class="form-group mb-3">
          <label class="font-weight-bold mb-1" style="font-size: 12px; color: #4a5568;">
            <i class="fa fa-calendar mr-1 text-primary"></i>
            {{ $t('calculationMenu.year') || 'Cycle Year' }} <span class="text-danger">*</span>
          </label>
          <a-select
            v-model="calcModel.cycle_year"
            class="w-100"
            size="large"
            @change="onCalcYearChange"
          >
            <a-select-option
              v-for="y in availableYears"
              :key="y"
              :value="y"
              :disabled="y < 2026"
            >
              {{ y }} <span v-if="y < 2026" class="text-muted" style="font-size: 11px;">({{ $t('calculationMenu.locked') || 'Locked' }})</span>
            </a-select-option>
          </a-select>
        </div>

        <!-- Cycle Month -->
        <div class="form-group mb-1">
          <label class="font-weight-bold mb-1" style="font-size: 12px; color: #4a5568;">
            <i class="fa fa-clock-o mr-1 text-primary"></i>
            {{ $t('calculationMenu.month') || 'Cycle Month' }} <span class="text-danger">*</span>
          </label>
          <a-select
            v-model="calcModel.cycle_month"
            class="w-100"
            size="large"
          >
            <a-select-option
              v-for="m in monthOptions"
              :key="m.value"
              :value="m.value"
              :disabled="isMonthLocked(calcModel.cycle_year, m.value)"
            >
              {{ m.label }} <span v-if="isMonthLocked(calcModel.cycle_year, m.value)" class="text-muted" style="font-size: 11px;">({{ $t('calculationMenu.locked') || 'Locked' }})</span>
            </a-select-option>
          </a-select>
        </div>
      </div>

      <template slot="footer">
        <ts-button
          class="mr-2"
          @click="closeCalculationModal"
        >
          {{ $t('cancel') || 'Cancel' }}
        </ts-button>
        <ts-button
          color="primary"
          :waiting="calculating"
          @click="submitCalculation"
        >
          <i class="fa fa-calculator mr-1"></i>
          {{ $t('calculationMenu.calculate') || 'Calculate' }}
        </ts-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import moment from 'moment'

export default {
  name: 'CalculationMenuIndex',
  data() {
    return {
      loading: false,
      exporting: false,
      submitting: false,

      // Month picker state
      isPickerOpen: false,
      pickerMode: ['month', 'month'],
      clickCount: 0,

      // Filter model matching StudentHeadcountExcel condition
      model: {
        branch_id: [],
        scoring_cycle: 'month',
        from_year: moment().year(),
        to_year: moment().year(),
        month_range: [moment().subtract(1, 'month'), moment()]
      },

      branches: [],

      // Calculation modal state
      showCalculationModal: false,
      calculating: false,
      calcModel: {
        cycle_year: moment().year(),
        cycle_month: moment().month() + 1
      },
      monthOptions: [
        { value: 1, label: '01 - January' },
        { value: 2, label: '02 - February' },
        { value: 3, label: '03 - March' },
        { value: 4, label: '04 - April' },
        { value: 5, label: '05 - May' },
        { value: 6, label: '06 - June' },
        { value: 7, label: '07 - July' },
        { value: 8, label: '08 - August' },
        { value: 9, label: '09 - September' },
        { value: 10, label: '10 - October' },
        { value: 11, label: '11 - November' },
        { value: 12, label: '12 - December' }
      ],

      // Report response data from StudentHeadcountViewModel
      reportData: null
    }
  },

  computed: {
    availableYears() {
      const currentYear = moment().year()
      const years = []
      for (let y = currentYear + 2; y >= currentYear - 7; y--) {
        years.push(y)
      }
      return years
    },

    yearOptions() {
      const currentYear = moment().year()
      const years = []
      for (let y = currentYear + 2; y >= currentYear - 7; y--) {
        years.push({
          value: y,
          label: `${y}`
        })
      }
      return years
    },

    isYearlyReport() {
      return this.reportData
        ? !!this.reportData.isYearly
        : this.model.scoring_cycle === 'yearly'
    },

    yearTitle() {
      return this.reportData ? this.reportData.yearTitle : ''
    },

    startLabel() {
      return this.reportData ? this.reportData.startLabel : ''
    },

    endLabel() {
      return this.reportData ? this.reportData.endLabel : ''
    },

    reportMonths() {
      if (this.reportData && this.reportData.months) {
        return this.reportData.months
      }
      return []
    },

    activeBranchGroups() {
      if (!this.reportData) {
        return []
      }
      if (this.isYearlyReport) {
        return this.reportData.groups || []
      }
      return this.reportData.branchGroups || []
    },

    monthlyColSpan() {
      if (!this.reportMonths || this.reportMonths.length === 0) return 9
      return 2 + 7 + (this.reportMonths.length - 1) * 6
    }
  },

  created() {
    this.initBranches()
  },

  methods: {
    ...mapActions('reportAcademic', ['getBranch']),
    ...mapActions('calculationMenu', {
      apiFetchData: 'fetchData',
      apiExportExcel: 'exportExcel',
      apiSaveManual: 'saveManual',
      apiCalculate: 'calculate'
    }),

    isMonthLocked(year, month) {
      const y = parseInt(year)
      const m = parseInt(month)
      return y < 2026 || (y === 2026 && m < 9)
    },

    onCalcYearChange(val) {
      const y = parseInt(val)
      if (y === 2026 && parseInt(this.calcModel.cycle_month) < 9) {
        this.calcModel.cycle_month = 9
      }
    },

    openCalculationModal() {
      let defaultYear = parseInt(this.model.from_year) || moment().year()
      let defaultMonth = 9
      if (this.model.month_range && this.model.month_range[0]) {
        defaultMonth = moment(this.model.month_range[0]).month() + 1
      }
      if (defaultYear < 2026) {
        defaultYear = 2026
      }
      if (defaultYear === 2026 && defaultMonth < 9) {
        defaultMonth = 9
      }
      this.calcModel = {
        cycle_year: defaultYear,
        cycle_month: defaultMonth
      }
      this.showCalculationModal = true
    },

    closeCalculationModal() {
      this.showCalculationModal = false
    },

    async submitCalculation() {
      if (!this.calcModel.cycle_year) {
        this.$toasted &&
          this.$toasted.warning(
            this.$t('calculationMenu.selectCycleYearPrompt') ||
              'Please select a cycle year'
          )
        return
      }
      if (!this.calcModel.cycle_month) {
        this.$toasted &&
          this.$toasted.warning(
            this.$t('calculationMenu.selectCycleMonthPrompt') ||
              'Please select a cycle month'
          )
        return
      }

      const year = parseInt(this.calcModel.cycle_year)
      const month = parseInt(this.calcModel.cycle_month)

      if (this.isMonthLocked(year, month)) {
        const warningMsg =
          this.$t('calculationMenu.lockedMonthNotice') ||
          'Recalculation is only allowed from September 2026 onward. Earlier months are locked.'
        this.$toasted && this.$toasted.warning(warningMsg)
        return
      }

      this.calculating = true
      try {
        const response = await this.apiCalculate({
          cycle_year: year,
          cycle_month: month
        })
        this.$toasted &&
          this.$toasted.success(
            response.message ||
              this.$t('calculationMenu.calculateSuccess') ||
              'Headcount calculated successfully.'
          )
        this.showCalculationModal = false
        await this.fetchData()
      } catch (error) {
        let rawMsg =
          (error && error.message) ||
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Error executing calculation'

        // Clean raw SQL driver / PDO wrapper noise if present
        if (typeof rawMsg === 'string') {
          const match = rawMsg.match(
            /General error:\s*\d+\s+(.*?)(?:\s*\[\d+\]|\s*\(severity|\s*\(SQL:|$)/i
          )
          if (match && match[1]) {
            rawMsg = match[1].trim()
          }
        }

        let friendlyMsg = rawMsg
        if (
          rawMsg.includes('September 2026 onward') ||
          rawMsg.includes('Earlier months are locked')
        ) {
          friendlyMsg =
            this.$t('calculationMenu.lockedMonthNotice') ||
            'Recalculation is only allowed from September 2026 onward. Earlier months are locked.'
        } else if (
          rawMsg.includes('have manual input') &&
          rawMsg.includes('no longer be recalculated')
        ) {
          const manualMatch = rawMsg.match(
            /(\d+)\s*row\(s\)\s*of\s*(\d{2}-\d{4})/i
          )
          if (manualMatch && this.$i18n && this.$i18n.locale === 'kh') {
            friendlyMsg = `ទិន្នន័យចំនួន ${manualMatch[1]} ជួរក្នុងខែ ${manualMatch[2]} ត្រូវបានបញ្ចូលដោយដៃ (Manual Input)។ ខែនេះត្រូវបានចាក់សោរ មិនអាចគណនាឡើងវិញបានទេ។`
          } else if (this.$i18n && this.$i18n.locale === 'kh') {
            friendlyMsg =
              this.$t('calculationMenu.manualInputNotice') ||
              'ខែនេះមានទិន្នន័យដែលបានបញ្ចូលដោយដៃ (Manual Input) រួចហើយ មិនអាចគណនាឡើងវិញបានទេ។'
          }
        }

        this.$toasted && this.$toasted.error(friendlyMsg)
      } finally {
        this.calculating = false
      }
    },

    async initBranches() {
      this.loading = true
      try {
        const response = await this.getBranch()
        this.branches = (response.data || []).map(el => ({
          value: el.branch_id,
          label: el.branch_name_en || el.branch_name_kh
        }))

        const defaultBranchId =
          this.$store &&
          this.$store.state &&
          this.$store.state.authUser &&
          this.$store.state.authUser.default_branch_id

        if (defaultBranchId) {
          this.model.branch_id = [defaultBranchId]
        } else if (this.branches.length > 0) {
          this.model.branch_id = [this.branches[0].value]
        }
        await this.fetchData()
      } catch (error) {
        this.$toasted &&
          this.$toasted.error(error.message || 'Error loading branches')
      } finally {
        this.loading = false
      }
    },

    getFormattedParams() {
      const isYearly = this.model.scoring_cycle === 'yearly'
      if (isYearly) {
        return {
          branch_id: this.model.branch_id,
          scoring_cycle: 'yearly',
          cycle: 'yearly',
          type: 'yearly',
          from_year: this.model.from_year,
          to_year: this.model.to_year,
          year: this.model.from_year
        }
      }
      const range = this.model.month_range || []
      return {
        branch_id: this.model.branch_id,
        scoring_cycle: 'month',
        cycle: 'month',
        type: 'month',
        from_month: range[0] ? moment(range[0]).format('YYYY-MM') : null,
        to_month: range[1] ? moment(range[1]).format('YYYY-MM') : null
      }
    },

    async fetchData() {
      this.loading = true
      try {
        const params = this.getFormattedParams()
        const data = await this.apiFetchData(params)
        this.reportData = data
      } catch (error) {
        this.$toasted &&
          this.$toasted.error(
            error.message || 'Error loading headcount calculation data'
          )
      } finally {
        this.loading = false
      }
    },

    async exportExcel() {
      this.exporting = true
      try {
        const params = this.getFormattedParams()
        const response = await this.apiExportExcel(params)
        const isYearly = this.model.scoring_cycle === 'yearly'
        const fileName = isYearly
          ? 'student-headcounts-yearly.xlsx'
          : 'student-headcounts.xlsx'
        FileDownload(
          response.data,
          fileName,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        )
      } catch (error) {
        this.$toasted &&
          this.$toasted.error(error.message || 'Error exporting excel')
      } finally {
        this.exporting = false
      }
    },

    buildItems() {
      if (!this.activeBranchGroups || this.activeBranchGroups.length === 0) {
        return []
      }
      const regularGroups = this.activeBranchGroups.filter(
        g => !g.is_grand_total
      )
      const items = []
      const programMap = { 'Pre-K': 1, EKP: 2, KH: 3, PEP: 4, Chinese: 5 }
      const serviceMap = {
        Lunch: 1,
        'Noon Care': 2,
        Computer: 3,
        'School Van': 4
      }

      regularGroups.forEach(group => {
        const branchId = group.branch_id
        if (!branchId) return

        // 1. Sub Programs (Module = 'P')
        const subPrograms = group.subPrograms || []
        subPrograms.forEach((row, rIndex) => {
          const groupId = row.acps_id || programMap[row.name] || rIndex + 1

          this.reportMonths.forEach(m => {
            const y = m.year
            const mo = m.month
            // Skip locked months before September 2026 (Store rule 50208)
            if (y < 2026 || (y === 2026 && mo < 9)) return

            const md = (row.months && row.months[m.key]) || {}
            const n = parseInt(md.new) || 0
            const d = parseInt(md.dropped) || 0
            const tin = parseInt(md.transfer_in) || 0
            const tout = parseInt(md.transfer_out) || 0
            const free = parseInt(md.free) || 0

            // Only send modified or non-zero entries
            if (
              md.is_modified ||
              n !== 0 ||
              d !== 0 ||
              tin !== 0 ||
              tout !== 0 ||
              free !== 0
            ) {
              items.push({
                module: 'P',
                branch_id: branchId,
                group_id: groupId,
                cycle_year: y,
                cycle_month: mo,
                new: n,
                dropped: d,
                transfer_in: tin,
                transfer_out: tout,
                free: free
              })
            }
          })
        })

        // 2. Services (Module = 'S')
        const services = group.services || []
        services.forEach((row, rIndex) => {
          const groupId =
            row.service_type_id || serviceMap[row.name] || rIndex + 1

          this.reportMonths.forEach(m => {
            const y = m.year
            const mo = m.month
            // Skip locked months before September 2026 (Store rule 50208)
            if (y < 2026 || (y === 2026 && mo < 9)) return

            const md = (row.months && row.months[m.key]) || {}
            const n = parseInt(md.new) || 0
            const d = parseInt(md.dropped) || 0
            const tin = parseInt(md.transfer_in) || 0
            const tout = parseInt(md.transfer_out) || 0
            const free = parseInt(md.free) || 0

            // Only send modified or non-zero entries
            if (
              md.is_modified ||
              n !== 0 ||
              d !== 0 ||
              tin !== 0 ||
              tout !== 0 ||
              free !== 0
            ) {
              items.push({
                module: 'S',
                branch_id: branchId,
                group_id: groupId,
                cycle_year: y,
                cycle_month: mo,
                new: n,
                dropped: d,
                transfer_in: tin,
                transfer_out: tout,
                free: free
              })
            }
          })
        })
      })

      return items
    },

    async submitManualHeadcount() {
      if (this.isYearlyReport) {
        this.$toasted &&
          this.$toasted.info(
            this.$t('calculationMenu.monthlyOnlySave') ||
              'Manual submit is supported in Monthly view.'
          )
        return
      }

      const allMonthsLocked =
        this.reportMonths.length > 0 &&
        this.reportMonths.every(
          m => m.year < 2026 || (m.year === 2026 && m.month < 9)
        )
      if (allMonthsLocked) {
        this.$toasted &&
          this.$toasted.warning(
            'Months before September 2026 are locked and cannot be changed.'
          )
        return
      }

      const items = this.buildItems()
      if (!items || items.length === 0) {
        this.$toasted &&
          this.$toasted.warning(
            this.$t('calculationMenu.nothingToSave') || 'Nothing to save.'
          )
        return
      }

      this.submitting = true
      try {
        const response = await this.apiSaveManual({
          items: items
        })
        this.$toasted &&
          this.$toasted.success(
            response.message || 'Action completed successfully!'
          )
        await this.fetchData()
      } catch (error) {
        this.$toasted &&
          this.$toasted.error(error.message || 'Error saving manual headcount')
      } finally {
        this.submitting = false
      }
    },

    onCycleChange(e) {
      const val = e && e.target ? e.target.value : e
      this.model.scoring_cycle = val
      this.fetchData()
    },

    onOpenChange(open) {
      this.isPickerOpen = open
      this.clickCount = 0
      if (open) {
        this.pickerMode = ['month', 'month']
      }
    },

    onPanelChange(value, mode) {
      this.model.month_range = value
      this.pickerMode = [
        mode[0] === 'date' ? 'month' : mode[0],
        mode[1] === 'date' ? 'month' : mode[1]
      ]
      this.clickCount++
      if (this.clickCount >= 2) {
        this.isPickerOpen = false
        this.clickCount = 0
        this.fetchData()
      }
    },

    onRangeChange(value) {
      this.model.month_range = value
      this.clickCount = 0
      this.fetchData()
    },

    getFilteredSubPrograms(group) {
      if (!group || !group.subPrograms) return []
      return group.subPrograms
    },

    getFilteredServices(group) {
      if (!group || !group.services) return []
      return group.services
    },

    formatVal(val) {
      if (val === null || val === undefined || val === 0 || val === '0') {
        return '-'
      }
      return Number(val).toLocaleString()
    },

    ensureRowMonth(row, mKey) {
      if (!row.months) {
        this.$set(row, 'months', {})
      }
      if (!row.months[mKey]) {
        this.$set(row.months, mKey, {
          old: 0,
          new: 0,
          dropped: 0,
          transfer_in: 0,
          transfer_out: 0,
          free: 0,
          total: 0
        })
      }
    },

    onMonthlyCellChange(event, group, row, mKey, field) {
      const raw = event.target.value
      const val = raw === '' ? '' : parseInt(raw) || 0
      this.ensureRowMonth(row, mKey)
      this.$set(row.months[mKey], field, val)
      this.$set(row.months[mKey], 'is_modified', true)

      // Rechain all months for this row (matches pc_rechain_student_headcount in store)
      let currentOld = parseInt(row.first_old) || 0
      this.reportMonths.forEach(m => {
        this.ensureRowMonth(row, m.key)
        this.$set(row.months[m.key], 'old', currentOld)
        const n = parseInt(row.months[m.key].new) || 0
        const d = parseInt(row.months[m.key].dropped) || 0
        const cin = parseInt(row.months[m.key].transfer_in) || 0
        const cout = parseInt(row.months[m.key].transfer_out) || 0
        const tot = currentOld + n - d + cin - cout
        this.$set(row.months[m.key], 'total', tot)
        currentOld = tot
      })

      this.recalculateMonthlyTotals()
    },

    onMonthlyCellBlur(event, group, row, mKey, field) {
      if (row.months && row.months[mKey] && row.months[mKey][field] === '') {
        this.$set(row.months[mKey], field, 0)
        let currentOld = parseInt(row.first_old) || 0
        this.reportMonths.forEach(m => {
          this.ensureRowMonth(row, m.key)
          this.$set(row.months[m.key], 'old', currentOld)
          const n = parseInt(row.months[m.key].new) || 0
          const d = parseInt(row.months[m.key].dropped) || 0
          const cin = parseInt(row.months[m.key].transfer_in) || 0
          const cout = parseInt(row.months[m.key].transfer_out) || 0
          const tot = currentOld + n - d + cin - cout
          this.$set(row.months[m.key], 'total', tot)
          currentOld = tot
        })
        this.recalculateMonthlyTotals()
      }
    },

    recalculateMonthlyTotals() {
      if (!this.reportData || !this.reportData.branchGroups) return
      const months = this.reportMonths
      const regularGroups = this.reportData.branchGroups.filter(
        g => !g.is_grand_total
      )
      const grandGroup = this.reportData.branchGroups.find(
        g => g.is_grand_total
      )

      const sections = [
        { rowKey: 'subPrograms', totalsKey: 'subProgramTotals' },
        { rowKey: 'services', totalsKey: 'serviceTotals' }
      ]

      sections.forEach(section => {
        // 1. Recalculate branch totals for each regular group
        regularGroups.forEach(group => {
          const rows = group[section.rowKey] || []
          const totals = {
            first_old: 0,
            months: {}
          }

          months.forEach(m => {
            totals.months[m.key] = {
              old: 0,
              new: 0,
              dropped: 0,
              transfer_in: 0,
              transfer_out: 0,
              free: 0,
              total: 0
            }
          })

          rows.forEach(r => {
            totals.first_old += parseInt(r.first_old) || 0
            months.forEach(m => {
              const md = (r.months && r.months[m.key]) || {}
              totals.months[m.key].old += parseInt(md.old) || 0
              totals.months[m.key].new += parseInt(md.new) || 0
              totals.months[m.key].dropped += parseInt(md.dropped) || 0
              totals.months[m.key].transfer_in += parseInt(md.transfer_in) || 0
              totals.months[m.key].transfer_out +=
                parseInt(md.transfer_out) || 0
              totals.months[m.key].free += parseInt(md.free) || 0
              totals.months[m.key].total += parseInt(md.total) || 0
            })
          })

          this.$set(group, section.totalsKey, totals)
        })

        // 2. Recalculate Grand Total group (if present)
        if (grandGroup) {
          const grandRows = grandGroup[section.rowKey] || []
          const grandTotals = {
            first_old: 0,
            months: {}
          }

          months.forEach(m => {
            grandTotals.months[m.key] = {
              old: 0,
              new: 0,
              dropped: 0,
              transfer_in: 0,
              transfer_out: 0,
              free: 0,
              total: 0
            }
          })

          grandRows.forEach(gRow => {
            let rFirstOld = 0
            const rMonths = {}
            months.forEach(m => {
              rMonths[m.key] = {
                old: 0,
                new: 0,
                dropped: 0,
                transfer_in: 0,
                transfer_out: 0,
                free: 0,
                total: 0
              }
            })

            regularGroups.forEach(g => {
              const match = (g[section.rowKey] || []).find(
                r => r.name === gRow.name
              )
              if (match) {
                rFirstOld += parseInt(match.first_old) || 0
                months.forEach(m => {
                  const md = (match.months && match.months[m.key]) || {}
                  rMonths[m.key].old += parseInt(md.old) || 0
                  rMonths[m.key].new += parseInt(md.new) || 0
                  rMonths[m.key].dropped += parseInt(md.dropped) || 0
                  rMonths[m.key].transfer_in += parseInt(md.transfer_in) || 0
                  rMonths[m.key].transfer_out += parseInt(md.transfer_out) || 0
                  rMonths[m.key].free += parseInt(md.free) || 0
                  rMonths[m.key].total += parseInt(md.total) || 0
                })
              }
            })

            gRow.first_old = rFirstOld
            this.$set(gRow, 'months', rMonths)

            grandTotals.first_old += rFirstOld
            months.forEach(m => {
              grandTotals.months[m.key].old += rMonths[m.key].old
              grandTotals.months[m.key].new += rMonths[m.key].new
              grandTotals.months[m.key].dropped += rMonths[m.key].dropped
              grandTotals.months[m.key].transfer_in +=
                rMonths[m.key].transfer_in
              grandTotals.months[m.key].transfer_out +=
                rMonths[m.key].transfer_out
              grandTotals.months[m.key].free += rMonths[m.key].free
              grandTotals.months[m.key].total += rMonths[m.key].total
            })
          })

          this.$set(grandGroup, section.totalsKey, grandTotals)
        }
      })
    }
  }
}
</script>

<style scoped>
.filter-panel {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.filter-label {
  font-size: 11px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 5px;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.headcount-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.headcount-table th,
.headcount-table td {
  border: 1px solid #8c8c8c;
  padding: 4px 6px;
  vertical-align: middle;
}

.th-fixed {
  background-color: #e7e6e6;
  font-weight: bold;
  color: #000000;
}

.month-header {
  background-color: #e7e6e6;
  font-weight: bold;
  color: #000000;
}

.sub-header th {
  background-color: #ffffff;
  font-size: 10.5px;
  font-weight: bold;
}

.col-old {
  color: #002060 !important;
  font-weight: bold;
  text-align: center !important;
}

.col-new {
  color: #c00000 !important;
  font-weight: bold;
  text-align: center !important;
}

.col-drop {
  color: #004085 !important;
  font-weight: 600;
  text-align: center !important;
}

.col-cin {
  color: #375623 !important;
  font-weight: 600;
  text-align: center !important;
}

.col-cout {
  color: #833c0c !important;
  font-weight: 600;
  text-align: center !important;
}

.col-free {
  color: #375623 !important;
  font-weight: 600;
  text-align: center !important;
}

.col-total {
  color: #002060 !important;
  font-weight: bold;
  text-align: center !important;
}

.headcount-table td.col-old,
.headcount-table td.col-new,
.headcount-table td.col-drop,
.headcount-table td.col-cin,
.headcount-table td.col-cout,
.headcount-table td.col-free,
.headcount-table td.col-total {
  text-align: center !important;
  vertical-align: middle !important;
}

.headcount-table td.p-0 {
  padding: 0 !important;
  text-align: center !important;
  vertical-align: middle !important;
}

.total-row td {
  background-color: #cccccc !important;
  font-weight: bold;
  text-align: center;
}

.total-row td:first-child {
  text-align: left !important;
}

.cell-input {
  display: block;
  width: 100% !important;
  height: 28px;
  line-height: 28px;
  padding: 0 2px;
  margin: 0 auto;
  border: 1px solid transparent;
  border-radius: 2px;
  background-color: transparent;
  text-align: center !important;
  font-size: 11px;
  font-weight: inherit;
  color: inherit;
  transition: all 0.15s ease-in-out;
  outline: none;
  box-sizing: border-box;
}

.cell-input:hover {
  background-color: #ffffff;
  border-color: #b3d8ff;
}

.cell-input:focus {
  background-color: #ffffff;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.cell-input::-webkit-outer-spin-button,
.cell-input::-webkit-inner-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
  display: none !important;
}

.cell-input {
  -moz-appearance: textfield !important;
  text-align: center !important;
}

.cell-span {
  display: block;
  width: 100%;
  height: 28px;
  line-height: 28px;
  text-align: center !important;
  margin: 0 auto;
}
</style>
